import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

const NUM = /-?[\d.]+(?:,\d+)?/;

interface ValorPartido {
    n: number;
    dec: number;
    pre: string;
    suf: string;
}

/** Parte "R$ 148.320,00" em prefixo / número / sufixo, para animar só os dígitos. */
function parseValue(v: unknown): ValorPartido | null {
    if (typeof v !== 'string') return null;
    const m = v.match(NUM);
    if (!m || m.index === undefined) return null;
    const raw = m[0];
    const n = parseFloat(raw.replace(/\./g, '').replace(',', '.'));
    if (!isFinite(n)) return null;
    const dec = raw.indexOf(',') > -1 ? (raw.split(',')[1]?.length ?? 0) : 0;
    return { n, dec, pre: v.slice(0, m.index), suf: v.slice(m.index + raw.length) };
}

/**
 * Conta de 0 até o valor. O NÚMERO FINAL NUNCA DEPENDE DA ANIMAÇÃO:
 * o estado nasce no destino, e a contagem só começa quando o documento
 * está visível e o motion é permitido. Aba em segundo plano, iframe oculto,
 * prerender, captura headless e impressão em PDF não rodam requestAnimationFrame —
 * nesses casos o valor correto já está na tela desde o primeiro paint.
 */
function useCountUp(target: number, dec: number, on: boolean): string {
    const [v, setV] = useState(target);

    useEffect(() => {
        if (!on) {
            setV(target);
            return;
        }
        const semMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (semMotion || document.hidden || typeof requestAnimationFrame !== 'function') {
            setV(target);
            return;
        }

        let raf = 0;
        let t0 = 0;
        const dur = 620;
        const chegar = () => setV(target);
        const step = (t: number) => {
            if (!t0) t0 = t;
            const p = Math.min(1, (t - t0) / dur);
            setV(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(step);
            else chegar();
        };

        setV(0);
        raf = requestAnimationFrame(step);
        /* Rede de segurança: se o rAF for suspenso no meio, o destino chega por timer. */
        const garantia = setTimeout(chegar, dur + 120);
        document.addEventListener('visibilitychange', chegar);

        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(garantia);
            document.removeEventListener('visibilitychange', chegar);
        };
    }, [target, on]);

    return v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

export type KpiDeltaTone = 'up' | 'down' | 'neutral';

const deltaTones: Record<KpiDeltaTone, string> = {
    up: 'var(--success-strong)',
    down: 'var(--danger-strong)',
    neutral: 'var(--text-secondary)',
};

export interface KpiCardProps {
    label?: ReactNode;
    /** Texto já formatado, ex.: "R$ 148.320,00". Só os dígitos animam. */
    value?: ReactNode;
    delta?: ReactNode;
    deltaTone?: KpiDeltaTone;
    hint?: ReactNode;
    icon?: ReactNode;
    animate?: boolean;
    style?: CSSProperties;
}

export function KpiCard({
    label, value, delta, deltaTone = 'neutral', hint, icon, animate = true, style,
}: KpiCardProps) {
    const tone = deltaTones[deltaTone] ?? deltaTones.neutral;
    const parsed = animate ? parseValue(value) : null;
    const shown = useCountUp(parsed ? parsed.n : 0, parsed ? parsed.dec : 0, Boolean(parsed));
    const [hover, setHover] = useState(false);

    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                background: 'var(--surface)', border: `1px solid ${hover ? 'var(--border-strong)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-card)', padding: 18, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0,
                transition: 'border-color var(--dur-base) var(--ease-standard), transform var(--dur-base) var(--ease-standard)',
                transform: hover ? 'translateY(-2px)' : 'none', ...style,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: 13, lineHeight: '18px', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
                {icon && (
                    <span style={{ color: hover ? 'var(--burgundy-600)' : 'var(--neutral-500)', display: 'flex', transition: 'color var(--dur-base) var(--ease-standard)' }}>
                        {icon}
                    </span>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-ui)', fontSize: 'clamp(20px,2.1vw,26px)', lineHeight: '32px', fontWeight: 600, letterSpacing: 'var(--tracking-tight)', color: 'var(--text-primary)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {parsed ? parsed.pre + shown + parsed.suf : value}
                </span>
                {delta && <span style={{ fontSize: 12.5, fontWeight: 600, color: tone }}>{delta}</span>}
            </div>
            {hint && <span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--text-tertiary)' }}>{hint}</span>}
        </div>
    );
}
